"use client";

import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib";

/**
 * Soft frosted plate for splash / hero side panels (e.g. Platform Action
 * active-plan card). Not the engagement {@link GlassPanel} rim — use that when
 * you need a translucent glass border band.
 *
 * `opaque` restores solid `bg-background` for entity-editor topdrawer columns
 * (pre-frosted HeaderPanel look). Default `frosted` keeps PA ActivePlan soft.
 */
export const SPLASH_PANEL_SURFACE_CLASS = cn(
	"flex flex-col justify-center gap-3 rounded-xl p-4",
	"bg-background/55 backdrop-blur-md",
	"shadow-[0_1px_2px_rgb(15_23_42/0.08),0_3px_10px_rgb(15_23_42/0.06)]",
	"ring-1 ring-inset ring-white/50 dark:ring-white/12",
);

export const SPLASH_PANEL_OPAQUE_SURFACE_CLASS = cn(
	"flex flex-col justify-center gap-3 rounded-xl p-4",
	"bg-background",
	"shadow-[0_1px_2px_rgb(15_23_42/0.08),0_3px_10px_rgb(15_23_42/0.06)]",
	"ring-1 ring-inset ring-border/60",
);

/** Eyebrow / section label inside {@link SplashPanel}. */
export const SPLASH_PANEL_TITLE_CLASS = cn(
	"text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground",
);

export type SplashPanelSurface = "frosted" | "opaque";

export interface SplashPanelProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	/** Default `frosted`. Entity editor columns use `opaque`. */
	surface?: SplashPanelSurface;
}

function SplashPanelRoot({
	className,
	children,
	surface = "frosted",
	...rest
}: SplashPanelProps) {
	return (
		<div
			className={cn(
				surface === "opaque"
					? SPLASH_PANEL_OPAQUE_SURFACE_CLASS
					: SPLASH_PANEL_SURFACE_CLASS,
				className,
			)}
			data-slot="splash-panel"
			data-surface={surface}
			{...rest}
		>
			{children}
		</div>
	);
}

export interface SplashPanelHeaderProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

export function SplashPanelHeader({
	className,
	children,
	...rest
}: SplashPanelHeaderProps) {
	return (
		<div
			className={cn("flex min-w-0 flex-col gap-1", className)}
			data-slot="splash-panel-header"
			{...rest}
		>
			{children}
		</div>
	);
}

export interface SplashPanelTitleProps
	extends HTMLAttributes<HTMLParagraphElement> {
	children: ReactNode;
}

export function SplashPanelTitle({
	className,
	children,
	...rest
}: SplashPanelTitleProps) {
	return (
		<p
			className={cn(SPLASH_PANEL_TITLE_CLASS, className)}
			data-slot="splash-panel-title"
			{...rest}
		>
			{children}
		</p>
	);
}

export interface SplashPanelContentProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

export function SplashPanelContent({
	className,
	children,
	...rest
}: SplashPanelContentProps) {
	return (
		<div
			className={cn("flex min-w-0 flex-col gap-3", className)}
			data-slot="splash-panel-content"
			{...rest}
		>
			{children}
		</div>
	);
}

export interface SplashPanelActionsProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

export function SplashPanelActions({
	className,
	children,
	...rest
}: SplashPanelActionsProps) {
	return (
		<div
			className={cn("flex w-full flex-col gap-2", className)}
			data-slot="splash-panel-actions"
			{...rest}
		>
			{children}
		</div>
	);
}

export interface SplashPanelStickyHeaderProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

/** Sticky title / badge row for editor column panels (pair with ScrollBody + StickyFooter). */
export function SplashPanelStickyHeader({
	className,
	children,
	...rest
}: SplashPanelStickyHeaderProps) {
	return (
		<div
			className={cn("min-w-0 shrink-0", className)}
			data-slot="splash-panel-sticky-header"
			{...rest}
		>
			{children}
		</div>
	);
}

export interface SplashPanelScrollBodyProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

/** Scrollable middle section between sticky header and footer. */
export function SplashPanelScrollBody({
	className,
	children,
	...rest
}: SplashPanelScrollBodyProps) {
	return (
		<div
			className={cn("min-h-0 min-w-0 flex-1 overflow-y-auto", className)}
			data-slot="splash-panel-scroll-body"
			{...rest}
		>
			{children}
		</div>
	);
}

export interface SplashPanelStickyFooterProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

/** Sticky CTA footer for editor column panels. */
export function SplashPanelStickyFooter({
	className,
	children,
	...rest
}: SplashPanelStickyFooterProps) {
	return (
		<div
			className={cn("min-w-0 shrink-0", className)}
			data-slot="splash-panel-sticky-footer"
			{...rest}
		>
			{children}
		</div>
	);
}

/**
 * Compound splash / hero plate: shell + optional Header / Title / Content / Actions
 * and editor StickyHeader / ScrollBody / StickyFooter.
 *
 * @example
 * ```tsx
 * <SplashPanel>
 *   <SplashPanel.Title>Active plan</SplashPanel.Title>
 *   <SplashPanel.Content>…</SplashPanel.Content>
 *   <SplashPanel.Actions>
 *     <PrimaryCta>Resume</PrimaryCta>
 *   </SplashPanel.Actions>
 * </SplashPanel>
 * ```
 */
export const SplashPanel = Object.assign(SplashPanelRoot, {
	Header: SplashPanelHeader,
	Title: SplashPanelTitle,
	Content: SplashPanelContent,
	Actions: SplashPanelActions,
	StickyHeader: SplashPanelStickyHeader,
	ScrollBody: SplashPanelScrollBody,
	StickyFooter: SplashPanelStickyFooter,
});
